import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import capitalize from 'lodash/capitalize'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import { cn } from '@/lib/utils'
import CharacterClassSelect from 'app/frontend/base/components/CharacterClassSelect'
import CharacterSpecSelect, {
  parseRoleOrSpec,
} from 'app/frontend/base/components/CharacterSpecSelect'
import CharacterClass, {
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import { ManualMemberParams } from 'app/frontend/base/api/team'

// Mirrors Character::MANUAL_NAME_FORMAT.
const MANUAL_NAME_FORMAT = new RegExp('^\\p{L}{2,12}$', 'u')

const parseFullName = (term: string) => {
  const parts = term.split(/\s+/).filter(Boolean)
  if (parts.length !== 2) return null
  if (!parts.every((part) => MANUAL_NAME_FORMAT.test(part))) return null

  const [name, surname] = parts.map((part) => capitalize(part))
  return { name, surname }
}

type Props = {
  searchTerm: string
  onSubmit: (
    params: Omit<ManualMemberParams, 'teamRankId'>,
    onSuccess: () => void,
  ) => void
  onCreated: () => void
  isPending?: boolean
  disabled?: boolean
}

const NewManualCharacterForm = ({
  searchTerm,
  onSubmit,
  onCreated,
  isPending,
  disabled,
}: Props) => {
  const { t } = useTranslation()
  const [characterClass, setCharacterClass] = useState<CharacterClass>()
  const [roleOrSpec, setRoleOrSpec] = useState<string>()

  const identity = parseFullName(searchTerm)

  if (!identity) {
    return (
      <p className='pb-6 text-center text-sm text-muted-foreground'>
        {t('dashboard.team.roster.new-character-full-name-hint')}
      </p>
    )
  }

  const submit = () => {
    if (!characterClass || !roleOrSpec) return

    onSubmit(
      {
        ...identity,
        characterClass,
        ...parseRoleOrSpec(roleOrSpec),
      },
      () => {
        setCharacterClass(undefined)
        setRoleOrSpec(undefined)
        onCreated()
      },
    )
  }

  return (
    <Card className='mx-2 mb-2 p-3 flex flex-wrap items-center gap-2'>
      <span
        className={cn(
          'grow font-semibold whitespace-nowrap',
          characterClass && classColours[characterClass].text,
        )}
      >
        {identity.name} {identity.surname}
      </span>
      <CharacterClassSelect
        value={characterClass}
        setValue={(value) => {
          setCharacterClass(value)
          setRoleOrSpec(undefined)
        }}
        inModal
        className='w-40'
      />
      <CharacterSpecSelect
        characterClass={characterClass}
        value={roleOrSpec}
        setValue={setRoleOrSpec}
        includeRoles
        inModal
        className='w-56'
      />
      <Button
        size='sm'
        disabled={!characterClass || !roleOrSpec || disabled}
        loading={isPending}
        onClick={submit}
      >
        {t('dashboard.team.roster.add-as-new-character')}
      </Button>
    </Card>
  )
}

export default NewManualCharacterForm
